import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupsIcon from '@mui/icons-material/Groups';
import config from '../config';
import RuleScreen from '../shared/RuleScreen';
import FilterBar from '../shared/FilterBar';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import DetailDrawer from '../shared/DetailDrawer';
import { buildParams } from '../shared/apiParams';

const API_URL = config.apiUrl;
const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const calcDuration = (start, end) => {
  if (!start || !end) return 'N/A';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms)) return 'Invalid';
  const min = Math.abs(ms / 60000);
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'block', label: 'By Block', col: 'Block Name' },
  { key: 'gp', label: 'By Gram Panchayat', col: 'Gram Panchayat' },
  { key: 'village', label: 'By Village', col: 'Village Name' },
];

const pctCell = (b, highlight) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: highlight ? 'var(--danger)' : 'var(--muted)', fontWeight: highlight ? 700 : 400, marginLeft: 4 }}>
      ({b?.pct || 0}%)
    </span>
  </>
);

export default function Indicators({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [allFields, setAllFields] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, field: null, value: null, records: [], loading: false });
  const [recordPopup, setRecordPopup] = useState({ open: false, record: null });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/indicators/analytics?${params}`);
      if (res.data?.success) {
        setAllFields(res.data.fields || []);
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching indicators data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // A rule with a fieldKey narrows this generic multi-field page down to a
  // single-indicator dashboard (e.g. just "Skill Training Required").
  const fields = rule?.fieldKey ? allFields.filter((f) => f.key === rule.fieldKey) : allFields;

  const totalSurveys = summary?.totalSurveys ?? rows.reduce((s, r) => s + (Number(r.totalSurveys) || 0), 0);

  const tiles = (
    <>
      <StatTile label="Total Surveys" value={totalSurveys} icon={GroupsIcon} accent="var(--primary)" />
      {fields.map((f) => {
        const noPct = summary?.fields?.[f.key]?.no?.pct ?? 0;
        const yesPct = summary?.fields?.[f.key]?.yes?.pct ?? 0;
        const noDataPct = summary?.fields?.[f.key]?.noData?.pct ?? 0;
        const accent = noPct >= 50 ? 'var(--danger)' : noPct >= 25 ? 'var(--warning)' : 'var(--info)';
        const sub = noDataPct > 0 ? `${yesPct}% Yes · ${noDataPct}% No Data` : `${yesPct}% Yes`;
        return (
          <StatTile
            key={f.key}
            label={`${f.label} — No`}
            value={`${noPct}% No`}
            icon={FactCheckIcon}
            accent={accent}
            sub={sub}
          />
        );
      })}
    </>
  );

  const openDrilldown = async (row, field, value) => {
    setDrawer({ open: true, row, field, value, records: [], loading: true });
    try {
      const params = buildParams(applied, {
        extra: { groupBy: activeTab, groupValue: row.name, field, value, limit: 100 },
      });
      const res = await axios.get(`${API_URL}/indicators/details?${params}`);
      const records = res.data?.data || [];
      setDrawer({ open: true, row, field, value, records, loading: false });
    } catch (err) {
      console.error('Error fetching indicator detail records:', err);
      setDrawer({ open: true, row, field, value, records: [], loading: false });
    }
  };

  const indicatorCell = (row, fieldKey, value) => (
    <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(row, fieldKey, value); }}>
      {pctCell(row.fields?.[fieldKey]?.[value], value === 'no')}
    </span>
  );

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalSurveys', label: 'Total Surveys', align: 'center', render: (r) => <span className="cell-strong">{r.totalSurveys || 0}</span> },
    ...fields.flatMap((f) => {
      const hasBlanks = (summary?.fields?.[f.key]?.noData?.count ?? 0) > 0;
      const cols = [
        { key: `${f.key}_yes`, label: `${f.label} — Yes %`, align: 'center', render: (r) => indicatorCell(r, f.key, 'yes') },
        { key: `${f.key}_no`, label: `${f.label} — No %`, align: 'center', render: (r) => indicatorCell(r, f.key, 'no') },
      ];
      if (hasBlanks) {
        cols.push({ key: `${f.key}_noData`, label: `${f.label} — No Data %`, align: 'center', render: (r) => indicatorCell(r, f.key, 'noData') });
      }
      return cols;
    }),
  ];

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    { key: 'household_id', label: 'Household ID', render: (r) => r.household_id || '—' },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    { key: 'fieldValue', label: drawer.field?.label || 'Value', render: (r) => r.fieldValue ?? '—' },
    { key: 'block', label: 'Block', render: (r) => r.block || '—' },
    { key: 'village_name', label: 'Village', render: (r) => r.village_name || '—' },
    { key: 'start', label: 'Start Time', render: (r) => formatDate(r.start) },
    { key: 'end', label: 'End Time', render: (r) => formatDate(r.end) },
    { key: 'duration', label: 'Duration', align: 'center', render: (r) => calcDuration(r.start, r.end) },
  ];

  const filterBar = (
    <FilterBar
      onApply={(v) => { setApplied(v); setPage(1); }}
      onClear={() => { setApplied(EMPTY); setPage(1); }}
      loading={loading}
    />
  );

  return (
    <RuleScreen
      title={rule?.title || (fields.length === 1 ? fields[0].label : 'Household Indicators')}
      subtitle={fields.length === 1
        ? `Yes/No breakdown for ${fields[0].label}.`
        : 'Yes/No breakdown across Skill Training, Health Insurance, Agriculture, Livestock, and Own House.'}
      icon={FactCheckIcon}
      backTo={rule?.category ? `/category/${rule.category}` : undefined}
      backLabel="Back"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {filterBar}
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">{fields.length === 1 ? fields[0].label : 'Household Indicators'} by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          getRowKey={(r, i) => `${r.name}-${i}`}
        />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${drawer.field ? fields.find((f) => f.key === drawer.field)?.label : ''} (${drawer.value === 'yes' ? 'Yes' : drawer.value === 'noData' ? 'No Data' : 'No'})` : ''}
        sections={
          drawer.row
            ? [
                {
                  title: 'Household',
                  items: fields.map((f) => ({
                    label: f.label,
                    value: `${drawer.row.fields?.[f.key]?.yes?.count || 0} Yes / ${drawer.row.fields?.[f.key]?.no?.count || 0} No`,
                  })),
                },
              ]
            : []
        }
      >
        <div className="detail-section">
          <h4>Households</h4>
          <DataTable
            columns={drawerRecordColumns}
            rows={drawer.records}
            loading={drawer.loading}
            emptyMessage="No households found."
            onRowClick={(r) => setRecordPopup({ open: true, record: r })}
            getRowKey={(r, i) => r.uuid || i}
          />
        </div>
      </DetailDrawer>

      <DetailDrawer
        open={recordPopup.open}
        onClose={() => setRecordPopup((p) => ({ ...p, open: false }))}
        title={recordPopup.record ? `Household: ${recordPopup.record.household_id}` : ''}
        sections={
          recordPopup.record
            ? [
                {
                  title: 'Household',
                  items: [
                    { label: 'Household ID', value: recordPopup.record.household_id },
                    { label: 'Respondent', value: recordPopup.record.respondent_name },
                    { label: 'Enumerator', value: recordPopup.record.enumerator_id },
                    { label: 'Block', value: recordPopup.record.block },
                    { label: 'Gram Panchayat', value: recordPopup.record.gram_panchayat },
                    { label: 'Village', value: recordPopup.record.village_name },
                  ],
                },
                {
                  title: 'Survey Timing',
                  items: [
                    { label: 'Start Time', value: formatDate(recordPopup.record.start) },
                    { label: 'End Time', value: formatDate(recordPopup.record.end) },
                    { label: 'Duration', value: calcDuration(recordPopup.record.start, recordPopup.record.end) },
                  ],
                },
              ]
            : []
        }
      />
    </RuleScreen>
  );
}
